import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { filter, finalize } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateEventTypes, growthIndustryTopic } from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { StructuredJsonDisplayComponent } from '../../shared/design-system/structured-json-display.component';
import { GrowthAdminService, GrowthIndustryPackResponse } from './growth-admin.service';
import {
  growthIndustryCampaignFields,
  growthIndustryComplianceFields,
  growthIndustryFormRefFields,
  growthIndustryKpiFields,
  growthIndustryPlaybookStepFields,
  growthIndustryPromptFields,
  growthIndustrySegmentFields,
  industryPackArrayPayloads
} from './growth-industry-form.models';

@Component({
  standalone: true,
  selector: 'ain-growth-industry-pack-detail',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    RouterLink,
    FormsModule,
    ButtonModule,
    InputTextModule,
    StructuredJsonDisplayComponent
  ],
  template: `
    <ain-page [title]="pack()?.displayName ?? t('growth.industryPacks.detail.title')" [description]="t('growth.industryPacks.detail.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.industryPacks.detail.help.title'"
        [descriptionKey]="'growth.industryPacks.detail.help.description'"
        [bulletKeys]="['growth.industryPacks.detail.help.bullets.payloads', 'growth.industryPacks.detail.help.bullets.actions']" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (!pack()) {
        <ain-state type="empty" [title]="t('growth.industryPacks.detail.messages.notFound')" />
      } @else {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/growth/marketplace/installs']">{{ t('catalog.action.backToList') }}</a>
          <div class="toolbar-actions">
            @if (canInstall()) {
              <button
                pButton
                type="button"
                [label]="t('growth.industryPacks.install')"
                [loading]="actionType() === 'install'"
                (click)="install()"></button>
            }
            @if (canDisable()) {
              <button
                pButton
                type="button"
                severity="danger"
                [label]="t('growth.industryPacks.disable')"
                [loading]="actionType() === 'disable'"
                (click)="disable()"></button>
            }
            @if (canUpgrade()) {
              <button
                pButton
                type="button"
                severity="secondary"
                [label]="t('growth.industryPacks.upgrade')"
                [loading]="actionType() === 'upgrade'"
                (click)="upgrade()"></button>
            }
          </div>
        </div>

        <section class="card">
          <h3>{{ t('growth.industryPacks.detail.sections.metadata') }}</h3>
          <ain-info-hint
            [titleKey]="'growth.industryPacks.detail.help.metadata.title'"
            [descriptionKey]="'growth.industryPacks.detail.help.metadata.description'" />
          <div class="grid">
            <label>
              <span>{{ t('growth.industryPacks.columns.key') }}</span>
              <input pInputText type="text" [ngModel]="pack()!.key" [disabled]="true" />
            </label>
            <label>
              <span>{{ t('growth.industryPacks.columns.displayName') }}</span>
              <input pInputText type="text" [ngModel]="pack()!.displayName" [disabled]="true" />
            </label>
            <label>
              <span>{{ t('growth.industryPacks.columns.version') }}</span>
              <input pInputText type="text" [ngModel]="pack()!.version" [disabled]="true" />
            </label>
            <label>
              <span>{{ t('growth.industryPacks.columns.installedVersion') }}</span>
              <input pInputText type="text" [ngModel]="pack()!.installedVersion ?? '-'" [disabled]="true" />
            </label>
            <label>
              <span>{{ t('growth.industryPacks.columns.lifecycleStatus') }}</span>
              <input pInputText type="text" [ngModel]="lifecycleLabel(pack()!.lifecycleStatus)" [disabled]="true" />
            </label>
            <label>
              <span>{{ t('growth.industryPacks.columns.upgradeAvailable') }}</span>
              <input pInputText type="text" [ngModel]="pack()!.catalogUpgradeAvailable ? t('common.yes') : t('common.no')" [disabled]="true" />
            </label>
          </div>
        </section>

        <section class="card">
          <h3>{{ t('growth.industryPacks.detail.sections.kpi') }}</h3>
          <ain-info-hint [titleKey]="'growth.industryPacks.detail.help.kpi.title'" [descriptionKey]="'growth.industryPacks.detail.help.kpi.description'" />
          @if (kpiItems().length === 0) {
            <p class="muted">{{ t('growth.industryPacks.detail.emptySection') }}</p>
          } @else {
            @for (item of kpiItems(); track $index) {
              <article class="item-card">
                <ain-structured-json-display [fields]="kpiFields" [payloadJson]="item" />
              </article>
            }
          }
        </section>

        <section class="card">
          <h3>{{ t('growth.industryPacks.detail.sections.segments') }}</h3>
          <ain-info-hint [titleKey]="'growth.industryPacks.detail.help.segments.title'" [descriptionKey]="'growth.industryPacks.detail.help.segments.description'" />
          @if (segmentItems().length === 0) {
            <p class="muted">{{ t('growth.industryPacks.detail.emptySection') }}</p>
          } @else {
            @for (item of segmentItems(); track $index) {
              <article class="item-card">
                <ain-structured-json-display [fields]="segmentFields" [payloadJson]="item" />
              </article>
            }
          }
        </section>

        <section class="card">
          <h3>{{ t('growth.industryPacks.detail.sections.prompts') }}</h3>
          <ain-info-hint [titleKey]="'growth.industryPacks.detail.help.prompts.title'" [descriptionKey]="'growth.industryPacks.detail.help.prompts.description'" />
          @if (promptItems().length === 0) {
            <p class="muted">{{ t('growth.industryPacks.detail.emptySection') }}</p>
          } @else {
            @for (item of promptItems(); track $index) {
              <article class="item-card">
                <ain-structured-json-display [fields]="promptFields" [payloadJson]="item" />
              </article>
            }
          }
        </section>

        <section class="card">
          <h3>{{ t('growth.industryPacks.detail.sections.playbook') }}</h3>
          <ain-info-hint [titleKey]="'growth.industryPacks.detail.help.playbook.title'" [descriptionKey]="'growth.industryPacks.detail.help.playbook.description'" />
          @if (playbookItems().length === 0) {
            <p class="muted">{{ t('growth.industryPacks.detail.emptySection') }}</p>
          } @else {
            @for (item of playbookItems(); track $index) {
              <article class="item-card">
                <strong>{{ t('growth.industryPacks.detail.playbook.item') }} {{ $index + 1 }}</strong>
                <ain-structured-json-display [fields]="playbookFields" [payloadJson]="item" />
              </article>
            }
          }
        </section>

        <section class="card">
          <h3>{{ t('growth.industryPacks.detail.sections.campaigns') }}</h3>
          <ain-info-hint [titleKey]="'growth.industryPacks.detail.help.campaigns.title'" [descriptionKey]="'growth.industryPacks.detail.help.campaigns.description'" />
          @if (campaignItems().length === 0) {
            <p class="muted">{{ t('growth.industryPacks.detail.emptySection') }}</p>
          } @else {
            @for (item of campaignItems(); track $index) {
              <article class="item-card">
                <ain-structured-json-display [fields]="campaignFields" [payloadJson]="item" />
              </article>
            }
          }
        </section>

        <section class="card">
          <h3>{{ t('growth.industryPacks.detail.sections.forms') }}</h3>
          <ain-info-hint [titleKey]="'growth.industryPacks.detail.help.forms.title'" [descriptionKey]="'growth.industryPacks.detail.help.forms.description'" />
          @if (formItems().length === 0) {
            <p class="muted">{{ t('growth.industryPacks.detail.emptySection') }}</p>
          } @else {
            @for (item of formItems(); track $index) {
              <article class="item-card">
                <ain-structured-json-display [fields]="formRefFields" [payloadJson]="item" />
              </article>
            }
          }
        </section>

        <section class="card">
          <h3>{{ t('growth.industryPacks.detail.sections.compliance') }}</h3>
          <ain-info-hint [titleKey]="'growth.industryPacks.detail.help.compliance.title'" [descriptionKey]="'growth.industryPacks.detail.help.compliance.description'" />
          @if (complianceItems().length === 0) {
            <p class="muted">{{ t('growth.industryPacks.detail.emptySection') }}</p>
          } @else {
            @for (item of complianceItems(); track $index) {
              <article class="item-card">
                <ain-structured-json-display [fields]="complianceFields" [payloadJson]="item" />
              </article>
            }
          }
        </section>

        @if (errorMessage()) {
          <p class="message error">{{ errorMessage() }}</p>
        }
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; gap: 8px; flex-wrap: wrap; }
    .toolbar-actions { display: flex; gap: 8px; flex-wrap: wrap; }
    .back-link { color: var(--ain-color-primary); font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; margin-bottom: 12px; display: grid; gap: 10px; }
    .card h3 { margin: 0; }
    .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    label { display: grid; gap: 6px; }
    .item-card { border: 1px solid #eef2f7; border-radius: 8px; padding: 10px; display: grid; gap: 8px; }
    .muted { margin: 0; color: #64748b; font-size: 13px; }
    .message.error { color: #b91c1c; }
    @media (max-width: 960px) { .grid { grid-template-columns: 1fr 1fr; } }
    @media (max-width: 640px) { .grid { grid-template-columns: 1fr; } }
  `]
})
export class GrowthIndustryPackDetailComponent implements OnInit {
  private readonly api = inject(GrowthAdminService);
  private readonly localization = inject(LocalizationService);
  private readonly route = inject(ActivatedRoute);
  private readonly context = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly liveUpdateService = inject(LiveUpdateService);

  readonly loading = signal(true);
  readonly pack = signal<GrowthIndustryPackResponse | null>(null);
  readonly errorMessage = signal('');
  readonly actionType = signal<'install' | 'disable' | 'upgrade' | null>(null);

  readonly kpiFields = growthIndustryKpiFields;
  readonly segmentFields = growthIndustrySegmentFields;
  readonly promptFields = growthIndustryPromptFields;
  readonly playbookFields = growthIndustryPlaybookStepFields;
  readonly campaignFields = growthIndustryCampaignFields;
  readonly formRefFields = growthIndustryFormRefFields;
  readonly complianceFields = growthIndustryComplianceFields;

  readonly kpiItems = signal<string[]>([]);
  readonly segmentItems = signal<string[]>([]);
  readonly promptItems = signal<string[]>([]);
  readonly playbookItems = signal<string[]>([]);
  readonly campaignItems = signal<string[]>([]);
  readonly formItems = signal<string[]>([]);
  readonly complianceItems = signal<string[]>([]);

  private packKey = '';
  private subscribedTopic: string | null = null;

  ngOnInit(): void {
    this.packKey = this.route.snapshot.paramMap.get('packKey') ?? '';

    this.liveUpdateService.events$
      .pipe(
        filter(event => {
          if (event.eventType !== LiveUpdateEventTypes.growthIndustryChanged) {
            return false;
          }
          const payloadKey = event.payload['packKey'];
          return payloadKey === undefined || String(payloadKey).toLowerCase() === this.packKey.toLowerCase();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.load(false));

    this.destroyRef.onDestroy(() => {
      if (this.subscribedTopic) {
        void this.liveUpdateService.unsubscribe(this.subscribedTopic);
        this.subscribedTopic = null;
      }
    });
    void this.setupLiveSubscription();
    this.load(true);
  }

  lifecycleLabel(value: string): string {
    return this.t(`growth.industryPacks.lifecycle.${value}`);
  }

  canInstall(): boolean {
    const row = this.pack();
    return !!row && row.canInstall;
  }

  canDisable(): boolean {
    return this.pack()?.lifecycleStatus === 'Installed';
  }

  canUpgrade(): boolean {
    const row = this.pack();
    return !!row
      && row.catalogUpgradeAvailable
      && (row.lifecycleStatus === 'Installed' || row.lifecycleStatus === 'Disabled');
  }

  install(): void {
    this.runAction('install', () => this.api.installIndustryPack(this.packKey));
  }

  disable(): void {
    this.runAction('disable', () => this.api.disableIndustryPack(this.packKey));
  }

  upgrade(): void {
    this.runAction('upgrade', () => this.api.upgradeIndustryPack(this.packKey));
  }

  load(showLoading = true): void {
    if (!this.packKey) {
      this.loading.set(false);
      this.pack.set(null);
      return;
    }
    if (showLoading) {
      this.loading.set(true);
    }
    this.errorMessage.set('');
    this.api.getIndustryPack(this.packKey).subscribe({
      next: row => {
        this.pack.set(row);
        this.hydratePayloadSections(row);
        this.loading.set(false);
      },
      error: () => {
        this.pack.set(null);
        this.loading.set(false);
        this.errorMessage.set(this.t('growth.industryPacks.detail.messages.loadFailed'));
      }
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private hydratePayloadSections(row: GrowthIndustryPackResponse): void {
    this.kpiItems.set(industryPackArrayPayloads(row.kpiDefinitionsJson));
    this.segmentItems.set(industryPackArrayPayloads(row.segmentTemplatesJson));
    this.promptItems.set(industryPackArrayPayloads(row.promptRefsJson));
    this.playbookItems.set(industryPackArrayPayloads(row.growthPlaybookJson));
    this.campaignItems.set(industryPackArrayPayloads(row.campaignTemplatesJson));
    this.formItems.set(industryPackArrayPayloads(row.dynamicFormDefinitionRefsJson));
    this.complianceItems.set(industryPackArrayPayloads(row.complianceNotesJson));
  }

  private runAction(
    type: 'install' | 'disable' | 'upgrade',
    request: () => ReturnType<GrowthAdminService['installIndustryPack']>
  ): void {
    this.actionType.set(type);
    this.errorMessage.set('');
    request().pipe(finalize(() => this.actionType.set(null))).subscribe({
      next: row => {
        this.pack.set(row);
        this.hydratePayloadSections(row);
      },
      error: () => this.errorMessage.set(this.t(`growth.industryPacks.messages.${type}Failed`))
    });
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) {
      return;
    }
    this.subscribedTopic = growthIndustryTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }
}
